import { useRef } from 'react'
import type { DashboardFilters } from '@/features/dashboard/api/schemas'
import { InfoTip } from '@/shared/ui/InfoTip'
import { ClearCircleIcon } from '@/shared/ui/icons'
import { flagUrl } from './flags'
import { useDashboardFilters } from './useDashboardFilters'
import styles from './SectionFilters.module.css'

// The countries behind the grouped markets, as the info tooltip lists them.
const MARKET_GROUPS = `Greater Europe
AUT, NLD, IRL, GRC, PRT, BEL, LUX, FIN, EST, LVA, LTU, SVK, SVN

Other
POL, NOR, SWE, DNK, ISL, LIE, CHE`

/** Chips the dashboard names differently from Managerium. */
const MARKET_LABELS: Record<string, string> = { NEuro: 'Other' }

/**
 * The market chips, which filter the cards that take the market: "Markets:",
 * a chip per market (dark with the flag when selected), an (i) listing the
 * countries of the grouped markets and, once filtered, "Clear filters" on the
 * far right. They are the dashboard's: the same selection on every section and
 * deep dive.
 */
export const MarketChips = ({
  markets,
}: {
  markets: DashboardFilters['markets']
}) => {
  const { selectedMarkets, isMarketFiltered, toggleMarket, clearMarkets } =
    useDashboardFilters()
  const marketsRef = useRef<HTMLDivElement>(null)

  // The Clear button unmounts as it is used, so focus would fall to the body.
  const clear = () => {
    clearMarkets()
    marketsRef.current?.focus()
  }

  return (
    <div ref={marketsRef} tabIndex={-1} className={styles.markets}>
      <div className={styles.filters}>
        <span className={styles.label} aria-hidden='true'>
          Markets:
        </span>
        <div
          className={styles.chips}
          role='group'
          aria-label='Filter by market'
        >
          {markets.map(market => {
            const flag = market.flag ? flagUrl(market.flag) : undefined
            // With no market filter there is no pressed state to convey.
            const pressed = isMarketFiltered
              ? selectedMarkets.includes(market.id)
              : undefined
            return (
              <button
                key={market.id}
                type='button'
                className={`${styles.chip} ${flag ? styles.withFlag : styles.plain}`}
                aria-pressed={pressed}
                onClick={() => toggleMarket(market.id)}
              >
                {flag && <img src={flag} alt='' className={styles.flag} />}
                {MARKET_LABELS[market.id] ?? market.label}
              </button>
            )
          })}
        </div>
        <InfoTip name='market groups' description={MARKET_GROUPS} />
      </div>
      {isMarketFiltered && (
        <button type='button' className={styles.clear} onClick={clear}>
          Clear filters
          <ClearCircleIcon />
        </button>
      )}
    </div>
  )
}
